<script setup lang="ts">
  import { storeToRefs } from "pinia";
  import { useUserStore } from "~/stores/UserStore";
  import { usePlanStore } from "~/stores/PlanStore";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useTalkRoomEditStore } from "~/stores/TalkRoomEditStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { ContractMember } from "~/entities/ContractMember";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { TalkRoomEditInfo } from "~/entities/TalkRoomEditInfo";
  import { TALK_ROOM_DM_MAX } from "~/consts/TalkConfig";
  import { useHistoryStore } from "~/stores/HistoryStore";

  const userStore = useUserStore();
  const planStore = usePlanStore();
  const talkRoomStore = useTalkRoomStore();
  const talkRoomEditStore = useTalkRoomEditStore();
  const snackBarStore = useSnackBarStore();
  const historyStore = useHistoryStore();

  interface Props {
    isOwner?: boolean;
    planId: string;
    planInfo: PlanInfo;
    skipFetchingTalkRooms?: boolean;
    showDm?: boolean;
    from: "talk-room" | "user-detail";
    isTest?: boolean; // テスト時のみ true にする。でないと v-dialog を find できないため
  }
  const props = defineProps<Props>();
  const emit = defineEmits(["dmUpdate"]);

  const modal = ref(false);

  const contract = ref<ContractMember | null>(null);

  const openModal = async (userId: any) => {
    if (!userId) {
      sentryErrorLog(`UserProfileDialog.openModal: userId is invalid ${userId}`);
      return;
    }
    await userStore.fetchOther(userId, true);
    if (props.from === "talk-room") {
      await loadContract(userId);
    }

    modal.value = true;
  };

  const loadContract = async (userId: string) => {
    if (props.isOwner) {
      contract.value = await planStore.fetchMember(props.planId, userId);
    } else if (userStore.user!.userId === userStore.otherUser?.userId) {
      contract.value = await userStore.contractInfo(props.planId, true);
    }
  };

  defineExpose({
    openModal,
  });

  // すでにトークルームを取得している場合はここで取得しない (トークルームの負荷軽減のため)
  if (!props.skipFetchingTalkRooms) {
    talkRoomStore.getTalkRooms(props.planId);
  }
  const { talkRooms } = storeToRefs(talkRoomStore);

  const showDMButton = computed(() => {
    if (!props.showDm) {
      return false; // DMボタンを表示しない場合は表示しない
    }
    if (!props.isOwner) {
      return false; // 自分が講師でなければ表示しない
    }
    const targetUserId = userStore.otherUser!.userId!;
    if (props.planInfo.planOwner?.userId === targetUserId) {
      return false; // 相手がメイン講師であれば表示しない
    }
    if (props.planInfo.subOwnerIds.includes(targetUserId)) {
      return false; // 相手がサブ講師でも表示しない
    }
    if (!contract.value) {
      return false; // 契約情報がない場合は表示しない
    }
    if (isContractExpired(contract.value!)) {
      return false; // 契約終了済みの場合は表示しない
    }
    return true;
  });
  const dMTalkRoom = computed(() =>
    talkRooms.value.find(room => room.isDirectMessage && room.dmUser?.userId === contract.value?.clientUser.userId),
  );
  const hasDMTalkRoom = computed(() => {
    return !!dMTalkRoom.value;
  });
  const updateTalkRooms = async () => {
    // talkRoomStore.talkRooms を更新して他のコンポーネントがリアクティブに動くように
    await talkRoomStore.getTalkRooms(props.planId);
  };
  const onClickDM = async () => {
    modal.value = false;

    if (hasDMTalkRoom.value) {
      const talkRoomId = dMTalkRoom.value!.talkRoomId;
      if (dMTalkRoom.value!.isArchive) {
        if (talkRoomStore.isMaxDMTalkRooms()) {
          snackBarStore.setError(`ダイレクトメッセージは最大${TALK_ROOM_DM_MAX}個までです`);
          return;
        }
        // アーカイブから復旧
        const editInfo = TalkRoomEditInfo.fromTalkRoom(dMTalkRoom.value!);
        editInfo.isArchive = false;
        await talkRoomEditStore.updateTalkRoom(talkRoomId, editInfo);
        snackBarStore.set(`ダイレクトメッセージを復旧しました`);
        updateTalkRooms();
      } else {
        // ダイレクトメッセージへ
        historyStore.to(`/plan/${props.planId}?tab=talk&room=${talkRoomId}`);
      }
    } else {
      if (talkRoomStore.isMaxDMTalkRooms()) {
        snackBarStore.setError(`ダイレクトメッセージは最大${TALK_ROOM_DM_MAX}個までです`);
        return;
      }
      // ダイレクトメッセージ作成
      const editInfo = TalkRoomEditInfo.forDM(contract.value!.clientUser.userId, contract.value!.clientUser.userName);
      await talkRoomEditStore.createTalkRoom(props.planId, editInfo);
      snackBarStore.set(`ダイレクトメッセージを作成しました`);
      updateTalkRooms();
      emit("dmUpdate", editInfo.dmUserId);
    }
  };
  const buttonLabel = computed(() => {
    if (hasDMTalkRoom.value) {
      if (dMTalkRoom.value!.isArchive) {
        return "ダイレクトメッセージを復旧";
      } else {
        return "ダイレクトメッセージ";
      }
    }
    return "ダイレクトメッセージを作成";
  });

  // レスポンシブ対応
  const isSp = isSpDisplayRef();
</script>

<template>
  <v-dialog v-model="modal" :max-width="450" scrim="black" scrollable :attach="!!isTest">
    <v-card
      class="d-flex flex-column justify-center dialog-body"
      :class="[isSp ? 'py-4' : 'px-4 py-8']"
      data-testid="user-profile-dialog"
    >
      <div class="close-button" @click="modal = false">✕</div>
      <div class="user-icon mb-6">
        <AtomsUserIcon :src="userStore.otherUser?.userImage" :width="120" :height="120" />
      </div>
      <p class="user-name">{{ userStore.otherUser?.userName }}</p>

      <div v-if="showDMButton" class="mx-auto mt-6" data-testid="user-profile-dm-btn">
        <AtomsDialogButton @click="onClickDM">
          {{ buttonLabel }}
        </AtomsDialogButton>
      </div>

      <div v-if="!!userStore.otherUser?.userDescription">
        <v-divider class="mt-6" />
        <div class="user-bio scroll-y mt-6 px-6">
          {{ userStore.otherUser?.userDescription }}
        </div>
      </div>

      <div
        v-if="from === 'talk-room' && ((contract && isOwner) || userStore.user!.userId === userStore.otherUser?.userId)"
      >
        <v-divider class="mt-6" />
        <v-row justify="center" class="mt-4">
          <v-col class="pl-4 pr-1">
            <div class="footer-name">契約開始日</div>
            <div class="footer-value">
              {{ contract?.startDate ? dateToYYYYMMDDWithLocale(contract?.startDate) : "-" }}
            </div>
          </v-col>
          <v-col class="px-1">
            <div class="footer-name">契約終了日</div>
            <div class="footer-value">
              {{ contract?.endDate ? dateToYYYYMMDDWithLocale(contract?.endDate) : "-" }}
            </div>
          </v-col>
          <v-col class="pr-0 pl-1">
            <div class="footer-name">契約日数</div>
            <div class="footer-value">{{ contract?.contractDays || 0 }}日</div>
          </v-col>
        </v-row>
      </div>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .dialog-body {
    height: 100%;
    color: white;
    background-color: rgba(var(--v-theme-monochrome-04), 0.85);
  }
  .close-button {
    position: absolute;
    top: 0;
    right: 0;
    color: white;
    margin-top: 10px;
    margin-right: 15px;
    cursor: pointer;
  }
  .user-icon {
    margin: 0 auto;
  }
  .user-name {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1.8em;
    text-align: center;
  }
  .user-bio {
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.06em;
    line-height: 1.8em;
    max-height: 160px;
  }
  .footer-name {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1.8em;
    text-align: center;
    @include display_sp {
      font-size: 12px;
    }
  }
  .footer-value {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1.8em;
    text-align: center;
    @include display_sp {
      font-size: 14px;
    }
  }
</style>
